import EventEmitter from 'eventemitter3'
import validator from 'validator'

export default class Record extends EventEmitter {

	_data
	_errors = {}
	_proxy
	_dirty = false
	validation_schema = {}

	/**
	 * Creates a new Record instance.
	 * @param {Object} data - The initial data for the record.
	 * @param {Object} [validation_schema] - Optional validation schema for the record. Extended classes can also define their own validation_schema property, in that case, there is no need to pass it.
	 * @returns {Proxy} - A proxy to the record data.
	 */
	constructor (data, validation_schema) {
		super()

		// Record data
		this._data = data

		if (typeof validation_schema != 'undefined') {
			this.validation_schema = validation_schema
		}

		// We allow mutating the record data only through this proxy to facilitate validation
		let instance = this
		this._proxy = new Proxy(this._data, {

			get (data, field) {
				if (field == 'record') return instance
				if (field in data) return data[field]
				return undefined
			},

			set (data, field, value) {
				if (data[field] !== value) {
					data[field] = value			// Update value
					instance.validate(field)	// Run validation

					// Mark, that the record has been changed
					instance.setDirty()
				}

				// Updating a value always succeeds
				return true
			}
		})

		// If this is not an extended class, we override the reference
		if (new.target === Record) return this._proxy
	}

	/**
	 * Validates record field(s) against the validation schema.
	 *
	 * @param {string} [field] - Optional field name to validate. If undefined, validates all fields.
	 * @returns {boolean} - True if validation passes, false if there are errors.
	 *
	 * Validation rules can be:
	 * - 'required': Field must not be empty
	 * - [function, params, message]: Custom validation function
	 * - [validatorName, params, message]: Built-in validator function by name
	 *
	 * When a field is validated, errors are stored in _errors and a '{field}-updated' event is emitted.
	 */
	validate (field) {

		// If we have no specific field selected we validate all of them
		if (typeof field == 'undefined') {

			// Find all the unique field keys from the validation schema and the data itself
			let fields = Object.keys(this.validation_schema).concat(Object.keys(this._data)).filter((v, i, s) => s.indexOf(v) === i)

			// Validate each
			let valid = true
			for (let field of fields) {
				let field_valid = this.validate(field)
				valid = valid && field_valid
			}
			return valid
		}

		// Validate a specific field
		if (field in this.validation_schema) {

			let field_errors = []

			for (let rule of this.validation_schema[field]) {

				if (typeof rule == 'string') {

					// The rule is one of the primitive rules
					if (rule == 'required') {
						if (validator.isEmpty((this._data[field] === null ? '' : this._data[field] + ''), { ignore_whitespace: false }))
							field_errors.push('Nem lehet üres.')
						continue
					}
				}

				if (Array.isArray(rule)) {
					let [ vfunc, vparams, message ]	= rule

					// The rule refers to a function directly
					if (typeof vfunc == 'function') {
						if (!vfunc(this._data[field], vparams, this._data))
							field_errors.push(message)
						continue
					}

					// The rule refers to a validator function by name
					if (typeof vfunc == 'string') {
						if (!validator[vfunc]((this._data[field] === null ? '' : this._data[field] + ''), vparams))
							field_errors.push(message)
						continue
					}
				}
			}

			// Update the error state of this field
			if (field_errors.length > 0) {
				this._errors[field] = field_errors
			} else if (field in this._errors) {
				delete this._errors[field]
			}

			// Notify subscribed components that this field value or error state has changed
			this.emit(field + '-updated', this._data[field], this.getErrors(field))

			// Return true if we found no errors, false otherwise
			return field_errors.length == 0

		} else {

			// The field is not to be validated, but it may still have changed
			if (field in this._errors) delete this._errors[field]

			// Notify subscribed components that this field value or error state has changed
			this.emit(field + '-updated', this._data[field], this.getErrors(field))

			// No validation rules, so it's always considered valid
			return true
		}
	}

	/**
	 * Emits update events for all fields to notify subscribed components of the current state.
	 */
	updateBindings () {
		for (let field in this._data) {
			// Notify subscribed components of the current field value
			this.emit(field + '-updated', this._data[field], this.getErrors(field))
		}
	}

	/**
	 * Retrieves the validation errors for a specific field or all fields.
	 *
	 * @param {string} [field] - Optional field name to retrieve errors for. If undefined, retrieves errors for all fields.
	 * @returns {Array|string|null} - Array of error messages for the field, or an object of all errors, or null if no errors.
	 */
	getErrors (field) {
		if (typeof field == 'undefined') {
			return (Object.keys(this._errors).length > 0) ? this._errors : null
		} else {
			return (field in this._errors) ? this._errors[field] : null
		}
	}

	/**
	 * Clears the validation errors for a specific field or all fields.
	 *
	 * @param {string} [field] - Optional field name to clear errors for. If undefined, clears errors for all fields.
	 */
	clearErrors (field) {
		if (typeof field == 'undefined') {
			this._errors = {}
		} else {
			if (field in this._errors) {
				delete this._errors[field]
			}
		}

		this.updateBindings()
	}

	/**
	 * Sets the validation errors for a specific field.
	 *
	 * @param {string} field - The field name to set errors for.
	 * @param {Array} errors - Array of error messages to set for the field.
	 */
	setErrors (field, errors = []) {
		this._errors[field] = errors

		this.updateBindings()
	}

	/**
	 * Marks the record as dirty (changed) and emits a 'dirty-state' event when the state changes.
	 * This allows components to react to changes in the record's state, such as enabling or disabling save buttons.
	 * The dirty state is set to true whenever a field value is changed through the proxy, and can be reset to false after saving.
	 */
	setDirty () {
		if (!this._dirty) {
			this._dirty = true
			this.emit('dirty-state', true)
		}
	}

	/**
	 * Marks the record as not dirty (unchanged) and emits a 'dirty-state' event when the state changes.
	 * This allows components to react to changes in the record's state, such as enabling or disabling save buttons.
	 * The dirty state is set to false after saving or resetting the record.
	 */
	setNotDirty () {
		if (this._dirty) {
			this._dirty = false
			this.emit('dirty-state', false)
		}
	}

	/**
	 * Returns whether the record is dirty (has unsaved changes).
	 * @returns {boolean} - True if the record is dirty, false otherwise.
	 */
	get isDirty () {
		return this._dirty
	}

	/**
	 * Returns the underlying data object of the record.
	 * @returns {Object} - The data object of the record.
	 */
	getObject () {
		return this._data
	}
}
